import JSZip from "jszip";
import QRCodeStyling from "qr-code-styling";
import { getEpoch } from "../../utils/commonFunctions";
import QrTemplate from "../../assets/images/QrNewTemplate.png";
import { useEffect, useRef, useState } from "react";

/**
 * ScanMe QR template
 *
 * Uses the new blank rectangular template (card rounded, QR area left blank).
 * QR code, black frame, and serial number are generated dynamically.
 * Preview and ZIP download use the same design.
 *
 * Coordinates below were measured directly off QrNewTemplate.png's actual
 * pixels (card bbox, logo bottom edge), not assumed -- the image is
 * 1618 x 972, not the 1600 x 900 this file originally assumed.
 */

// Template dimensions (native size of QrNewTemplate.png)
const TPL_W = 1618;
const TPL_H = 972;

// QR frame position and dimensions -- centered in the card, below the logo
const FRAME_X = 106;
const FRAME_Y = 186;
const FRAME_SIZE = 446;

// QR code position and dimensions -- inset within the frame
const QR_X = 150;
const QR_Y = 230;
const QR_SIZE = 358;

// Serial number position
const SERIAL_CENTER_X = 329;
const SERIAL_BASELINE_Y = 667;

const baseOptions = {
    margin: 0,
    qrOptions: {
        errorCorrectionLevel: "L",
    },
    dotsOptions: {
        color: "#000000",
        type: "square",
    },
    cornersSquareOptions: {
        color: "#000000",
        type: "square",
    },
    cornersDotOptions: {
        color: "#000000",
        type: "square",
    },
};

function layout() {
    return {
        qrX: QR_X,
        qrY: QR_Y,
        qrSize: QR_SIZE,
        textX: SERIAL_CENTER_X,
        textY: SERIAL_BASELINE_Y,
    };
}

// Draw a clean, single-line rectangular QR frame.
function drawCleanQrFrame(ctx) {
    ctx.save();

    ctx.strokeStyle = "#000000";
    ctx.lineWidth = 3;

    // Half-pixel offset keeps the thin border crisp.
    ctx.strokeRect(
        FRAME_X + 0.5,
        FRAME_Y + 0.5,
        FRAME_SIZE - 1,
        FRAME_SIZE - 1
    );

    ctx.restore();
}

// Draw the serial number below the QR frame.
function drawSerial(ctx, text, l) {
    ctx.save();

    ctx.font = "bold 20px Arial";
    ctx.fillStyle = "#000000";
    ctx.textAlign = "center";
    ctx.textBaseline = "alphabetic";

    ctx.fillText(text, l.textX, l.textY);

    ctx.restore();
}

// Load an image and reject if it cannot be loaded.
function loadImage(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();

        img.onload = () => resolve(img);
        img.onerror = () => reject(
            new Error("Failed to load the QR template or QR code.")
        );

        img.src = src;
    });
}

// Generate the complete QR sticker.
async function generateQrCanvas(item) {
    if (!item?.qr) {
        throw new Error("QR code data is missing.");
    }

    const l = layout();

    const qrInst = new QRCodeStyling({
        ...baseOptions,
        width: l.qrSize,
        height: l.qrSize,
        data: item.qr,
    });

    const qrBlob = await qrInst.getRawData("png");
    const qrObjectUrl = URL.createObjectURL(qrBlob);

    try {
        const [templateImg, qrImg] = await Promise.all([
            loadImage(QrTemplate),
            loadImage(qrObjectUrl),
        ]);

        const canvas = document.createElement("canvas");
        canvas.width = TPL_W;
        canvas.height = TPL_H;

        const ctx = canvas.getContext("2d");

        if (!ctx) {
            throw new Error("Unable to create canvas context.");
        }

        // Draw the new blank template.
        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(0, 0, TPL_W, TPL_H);

        ctx.drawImage(
            templateImg,
            0,
            0,
            TPL_W,
            TPL_H
        );

        // Draw the clean rectangular QR frame.
        drawCleanQrFrame(ctx);

        // Draw the QR code inside the frame.
        ctx.drawImage(
            qrImg,
            l.qrX,
            l.qrY,
            l.qrSize,
            l.qrSize
        );

        // Draw the serial number.
        drawSerial(ctx, item.srNo || "", l);

        return canvas;
    } finally {
        URL.revokeObjectURL(qrObjectUrl);
    }
}

export default function UseCustomQR({ payload }) {
    const ref = useRef(null);
    const [canvasUrl, setCanvasUrl] = useState("");
    const [loading, setLoading] = useState(false);

    // Keep the hidden QR instance available, as in the original component.
    useEffect(() => {
        if (!ref.current) return;

        const qrCodeLive = new QRCodeStyling({
            width: 300,
            height: 300,
            ...baseOptions,
        });

        qrCodeLive.append(ref.current);

        return () => {
            if (ref.current) {
                ref.current.innerHTML = "";
            }
        };
    }, []);

    // Generate the preview whenever the payload changes.
    useEffect(() => {
        let cancelled = false;
        let previewUrl = "";

        const generatePreview = async () => {
            if (!payload?.qr) {
                setCanvasUrl("");
                return;
            }

            try {
                const canvas = await generateQrCanvas(payload);

                if (!cancelled) {
                    previewUrl = canvas.toDataURL("image/png");
                    setCanvasUrl(previewUrl);
                }
            } catch (error) {
                console.error("Failed to generate QR preview:", error);
            }
        };

        generatePreview();

        return () => {
            cancelled = true;
        };
    }, [payload]);

    // Download the QR sticker inside a ZIP file.
    const onDownload = async () => {
        if (!payload?.qr || loading) return;

        setLoading(true);

        try {
            const canvas = await generateQrCanvas(payload);

            const dataUrl = canvas
                .toDataURL("image/png")
                .split(",")[1];

            const zip = new JSZip();

            zip.file(
                `${payload.srNo || "qr-code"}.png`,
                dataUrl,
                { base64: true }
            );

            const content = await zip.generateAsync({
                type: "blob",
            });

            const downloadUrl = URL.createObjectURL(content);
            const link = document.createElement("a");

            link.href = downloadUrl;
            link.download = `${getEpoch()}.zip`;

            document.body.appendChild(link);
            link.click();
            link.remove();

            setTimeout(() => {
                URL.revokeObjectURL(downloadUrl);
            }, 1000);
        } catch (error) {
            console.error("Failed to download QR code:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: "100%",
            }}
        >
            {/* Hidden QR node */}
            <div
                ref={ref}
                style={{ display: "none" }}
            />

            {/* QR preview */}
            {canvasUrl && (
                <img
                    src={canvasUrl}
                    alt="ScanMe QR Code Sticker"
                    style={{
                        display: "block",
                        maxWidth: "100%",
                        height: "auto",
                        borderRadius: "0",
                    }}
                />
            )}

            {/* Download button */}
            <button
                type="button"
                onClick={onDownload}
                disabled={!payload?.qr || loading}
                style={{
                    marginTop: "16px",
                    width: "180px",
                    minHeight: "40px",
                    border: "none",
                    borderRadius: "4px",
                    backgroundColor: "#FFBE0B",
                    color: "#000000",
                    cursor: loading ? "wait" : "pointer",
                    opacity: loading ? 0.7 : 1,
                    fontSize: "14px",
                }}
            >
                {loading ? "Preparing..." : "Download"}
            </button>
        </div>
    );
}